import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomButton from "../../../components/CustomButton";
import Sidebar from "../../../components/Sidebar";
import { getHomePageInfos, getTradingInfos, updateTradingInfos } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import dropdownOptions from "../../../data/dropdownOptions.json";
import { getPermissionControllers, hasCustomerPermission, hasBrokerPermission } from "../../../utils/permissionHelper";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import "../../css/TradingInfromation.css";

export default function TradingInformation() {
  const { t } = useTranslation();
  const [brokerId, setBrokerId] = useState("");
  const [leverage, setLeverage] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const leverageOptions = dropdownOptions.leverageOptions;

  // Get permission controllers from user data
  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const {
    customerPerm,
    customerDevicePerm,
    brokerPerm,
    brokerDevicePerm,
  } = permissions;

  // Check permissions for editing leverage and broker_id
  // IMPORTANT: Only CUSTOMER CAN_UPDATE_LEVERAGE should control this on web (including IB users),
  // to match the intended behavior where enabling the broker permission alone is not enough.
  const canEditLeverage = hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_UPDATE_LEVERAGE
  );
  
  const canEditBrokerId = canEditLeverage; // Keep same gate if broker ID becomes editable later

  // Auto-dismiss messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Try to get user data from sessionStorage first (to avoid redundant API calls)
        let userDataFromStorage = null;
        try {
          const storedUserData = sessionStorage.getItem('userData');
          const storedTimestamp = sessionStorage.getItem('userDataTimestamp');
          
          // Use stored data if it exists and is less than 5 minutes old
          if (storedUserData && storedTimestamp) {
            const age = Date.now() - parseInt(storedTimestamp, 10);
            if (age < 5 * 60 * 1000) { // 5 minutes
              userDataFromStorage = JSON.parse(storedUserData);
              setUser(userDataFromStorage);
            }
          }
        } catch (storageError) {
        }
        
        // Only fetch from API if we don't have cached data
        if (!userDataFromStorage) {
          const userResponse = await getHomePageInfos();
          const userData = userResponse?.decryptedData || userResponse?.data;
          if (userData && userData.CODE === AppConstants.responseOk && userData.DATA) {
            setUser(userData.DATA);
            // Store in sessionStorage for future use
            try {
              sessionStorage.setItem('userData', JSON.stringify(userData.DATA));
              sessionStorage.setItem('userDataTimestamp', Date.now().toString());
            } catch (storageError) {
            }
          } else {
          }
        }

        // Fetch trading information
        const tradingResponse = await getTradingInfos();
        const tradingData = tradingResponse?.decryptedData || tradingResponse?.data;
        
        if (tradingData && tradingData.CODE === AppConstants.responseOk) {
          // Set broker_id if it exists - check for REFERAL_BROKER, BROKER_ID, or broker_id
          const brokerIdValue = tradingData.DATA?.REFERAL_BROKER || 
                                 tradingData.DATA?.BROKER_ID || 
                                 tradingData.DATA?.broker_id ||
                                 tradingData.DATA?.referal_broker;
          if (brokerIdValue) {
            setBrokerId(brokerIdValue.toString());
          } else {
          }
          
          // Set leverage if it exists - find matching option
          const leverageValue = tradingData.DATA?.LEVERAGE || tradingData.DATA?.leverage;
          if (leverageValue) {
            // Convert leverage to string and find matching option
            // API returns "100" or "200", but dropdown has "1:100" or "1:200"
            const leverageStr = leverageValue.toString();
            
            // Try to find by matching the number part (e.g., "100" matches "1:100")
            const matchingOption = leverageOptions.find(opt => {
              const optValue = opt.value || opt.label || '';
              // Extract number from "1:100" format or match directly
              if (optValue.includes(':')) {
                const numPart = optValue.split(':')[1];
                return numPart === leverageStr;
              }
              return optValue === leverageStr || opt.label === leverageStr;
            });
            if (matchingOption) {
              setLeverage(matchingOption);
            } else {
              // If not found, try to create a matching option from the value
              // If API returns "100", create option like { value: "1:100", label: "1:100" }
              const createdOption = leverageOptions.find(opt => {
                const optValue = opt.value || opt.label || '';
                if (optValue.includes(':')) {
                  return optValue.split(':')[1] === leverageStr;
                }
                return false;
              });
              
              if (createdOption) {
                setLeverage(createdOption);
              } else {
                // Fallback: set the value directly
                setLeverage(leverageValue);
              }
            }
          } else {
          }
        } else {
          // Set error message for user
          if (tradingData?.CODE === 401) {
            setError(t("auth_failed_login_again", { ns: "mobile" }));
          } else {
            setError(tradingData?.MESSAGE || `Error: CODE ${tradingData?.CODE || 'Unknown'}`);
          }
        }
      } catch (error) {
        if (error.response) {
        }
        setError(error.message || t("trading_info_fetch_failed", { ns: "mobile" }));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleBrokerIdChange = (e) => {
    setBrokerId(e.target.value);
  };

  const handleLeverageChange = (value) => {
    setLeverage(value);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setUpdating(true);
      setError(null);
      setSuccess(null);
      
      // Validate inputs - only leverage is required, broker_id can be null/empty
      if (!leverage) {
        setError(t("leverage_required", { ns: "mobile" }));
        setUpdating(false);
        return;
      }
      
      // Get leverage value (could be an object with value property or a string/number)
      let leverageValue;
      if (typeof leverage === 'object' && leverage !== null) {
        // Extract from object (e.g., { value: "1:100", label: "1:100" })
        leverageValue = leverage.value || leverage.label || leverage;
      } else {
        leverageValue = leverage;
      }
      
      // Extract numeric part from leverage (e.g., "1:100" -> "100", "1:200" -> "200")
      // If it's already just a number like "100" or 100, use it as is
      let leverageNumber = leverageValue;
      if (typeof leverageValue === 'string' && leverageValue.includes(':')) {
        // Extract number after the colon (e.g., "1:100" -> "100")
        const parts = leverageValue.split(':');
        leverageNumber = parts[parts.length - 1]; // Get the last part after colon
      } else if (typeof leverageValue === 'string') {
        // If it's already a number string like "100", use it
        leverageNumber = leverageValue;
      } else {
        // If it's a number, convert to string
        leverageNumber = leverageValue.toString();
      }
      
      // Broker ID can be null or empty - send null if empty
      const brokerIdValue = brokerId && brokerId.trim() !== "" ? brokerId.trim() : null;
      // Call update API with exact field names: REFERAL_BROKER and LEVERAGE
      // brokerIdValue can be null
      const response = await updateTradingInfos(brokerIdValue, leverageNumber);
      const result = response?.decryptedData || response?.data;
      if (result && result.CODE === AppConstants.responseOk) {
        setSuccess(t("trading_info_updated_successfully", { ns: "mobile" }));
        setError(null);
        setIsEditing(false);
      } else {
        setError(result?.MESSAGE || `Error: CODE ${result?.CODE || 'Unknown'}`);
        setSuccess(null);
      }
    } catch (error) {
      setError(error.message || t("trading_info_update_failed", { ns: "mobile" }));
      setSuccess(null);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="trading-information-page">
      <Sidebar user={user} />
      <div className="trading-information-content">
        <h1 className="trading-information-title">{t("trading_information", { ns: "mobile" })}</h1>
        {error && (
          <div style={{ 
            margin: "20px auto", 
            padding: "15px", 
            background: "#ff4444", 
            color: "white", 
            borderRadius: "8px",
            maxWidth: "600px",
            textAlign: "center"
          }}>
            <strong>{t("error", { ns: "mobile" })}:</strong> {error}
          </div>
        )}
        {success && (
          <div style={{ 
            margin: "20px auto", 
            padding: "15px", 
            background: "#4caf50", 
            color: "white", 
            borderRadius: "8px",
            maxWidth: "600px",
            textAlign: "center"
          }}>
            <strong>{t("success", { ns: "mobile" })}:</strong> {success}
          </div>
        )}
        {loading && (
          <div style={{ 
            margin: "20px auto", 
            padding: "15px", 
            textAlign: "center",
            color: "#ececea"
          }}>
            {t("loading", { ns: "mobile" })}
          </div>
        )}
        <div className="trading-information-container">
        <div className="form-group">
          <label className="form-label">{t("broker_id", { ns: "mobile" })}</label>
          <div className="input-wrapper">
            <span className="input-icon">
              <i className="fa-solid fa-user-tie"></i>
            </span>
            <CustomInput
              type="text"
              placeholder={t("broker_id", { ns: "mobile" })}
              value={brokerId}
              onChange={handleBrokerIdChange}
              className="trading-input"
              disabled={true}
            />
          </div>
        </div>

        <div className="form-group col-12">
          <label className="form-label">{t("leverage", { ns: "mobile" })}</label>
          <CustomDropdown
            options={leverageOptions}
            value={leverage}
            onChange={handleLeverageChange}
            placeholder={t("select_leverage", { ns: "mobile" })}
            className="trading-dropdown"
            disabled={!isEditing || !canEditLeverage}
          />
        </div>

        <CustomButton 
          className="edit-button"
          variant={isEditing ? "success" : "primary"}
          onClick={isEditing ? handleSave : handleEdit}
          disabled={updating || loading || !canEditLeverage}
        >
          {isEditing ? (
            <>
              <i className="fa-solid fa-check"></i>
              <span style={{ marginLeft: "8px" }}>{updating ? t("saving", { ns: "mobile" }) : t("save", { ns: "mobile" })}</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-pencil"></i>
              <span style={{ marginLeft: "8px" }}>{t("edit", { ns: "mobile" })}</span>
            </>
          )}
        </CustomButton>
        </div>
      </div>
    </div>
  );
}
